import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Card, CardBody, Container, Table, Button, Badge, Row, Col,
    Modal, ModalHeader, ModalBody, ModalFooter, Input, FormGroup, Label, Spinner,
} from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../api';
import useAuth from '../Personal Attendance/useAuth';
import {
    DashboardPageHeader, TableSkeleton, EmptyState, useDebouncedValue,
} from '../../Components/Common/DashboardKit';
import { exportRowsToCsv } from '../../Components/Common/DashboardKit/exportCsv';

/**
 * Approvals — the ONE screen for FTO / COA decisions (2026-09: replaces the
 * separate "FTO / COA Approvals" workbench at /attendance/approvals, which the
 * notification bell opens, and the HR Review queue below — both now render
 * this component).
 *
 *   • "My team"  (scope=mine) — FTO / COA of my direct reports waiting for my
 *     supervisor decision. Any signed-in user; empty if no direct reports.
 *   • "HR"       (scope=hr)   — everything waiting on HR (list below). HR / IT.
 *   Only requests starting in the last 90 days (or later) count as pending
 *   (PendingApprovalWindow.cs); an earlier From date shows older ones.
 *
 *   Data: GET /api/approvals/queue?scope=… (ApprovalsController).
 *   Each row carries a `gate` telling which decision Approve / Deny records:
 *     supervisor · hr · hr-override (no supervisor decision yet: HR decides
 *     for both, so the request is final — FTO deducted, e-mails sent).
 *
 * HR Review Dashboard — HR Round 1 (Issue 2).
 *
 * ONE queue for everything currently waiting on HR:
 *   • FTO requests awaiting HR approval
 *   • COA requests awaiting HR approval
 *   • Profile update requests pending review
 *   • Missing-punch requests pending approval
 *
 * Data: GET /api/dashboard/hr/review-queue — a read-only aggregation over
 * the SAME queues the individual pages already show (see
 * HrReviewQueueService.cs). Server-side filters + paging; HR/IT-gated
 * server-side (CanAccessHR), RoleProtected client-side in allRoutes.js.
 *
 * Actions REUSE the existing, already-authorized + audited endpoints:
 *   FTO      PATCH /api/fto/{id}/approvals   (record fetched first so
 *            supervisor/MPC/exemption fields are echoed back unchanged)
 *   COA      PATCH /api/coa/{id}/approvals   (same echo pattern)
 *   Punch    POST  /api/Punch/pending-approve | pending-deny
 *   Profile  "Review" navigates to /hr/profile-approvals — the profile
 *            decision needs the side-by-side old/new diff that page owns,
 *            so no blind inline approve is offered here.
 *
 * No new authority is granted by this page: a user who somehow reached it
 * without HR rights gets 403 from every call.
 */

const KIND_META = {
    All:          { label: 'All',            color: 'primary'  },
    FTO:          { label: 'FTO',            color: 'warning'  },
    COA:          { label: 'COA',            color: 'info'     },
    Profile:      { label: 'Profile',        color: 'success'  },
    MissingPunch: { label: 'Missing punch',  color: 'danger'   },
};

const kindBadge = (kind) => (
    <Badge color={KIND_META[kind]?.color || 'secondary'} pill>
        {KIND_META[kind]?.label || kind}
    </Badge>
);

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

// Round HR-PA (Part 4/7): the FTO and COA workspaces embed this SAME
// queue pre-filtered — one queue, one set of actions, just focused.
// `initialKind` only seeds the filter; the user can still widen it.
const HrReviewDashboard = ({ initialKind, initialScope }) => {
    document.title = 'Approvals | SSW Technologies Dashboard';

    // HR scope = same rule as the API's CanAccessHR policy (isHR || isItAdmin).
    // initialScope: 'mine' | 'hr' | 'auto' (bell page: supervisors land on My
    // team, HR-only users on HR). Default (HR workspaces): HR if allowed.
    const { isHR, isItAdmin, isManager } = useAuth();
    const canHr = !!(isHR || isItAdmin);
    const [scope, setScope] = useState(() => {
        if (!canHr || initialScope === 'mine') return 'mine';
        if (initialScope === 'auto') return isManager ? 'mine' : 'hr';
        return 'hr';
    });
    const isMine = scope === 'mine';

    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(false);

    // Filters
    const [kind, setKind]                 = useState(initialKind || 'All');
    const [dept, setDept]                 = useState('');
    const [supervisorId, setSupervisorId] = useState('');
    const [q, setQ]                       = useState('');
    const [from, setFrom]                 = useState('');
    const [to, setTo]                     = useState('');
    const [page, setPage]                 = useState(1);
    const pageSize = 25;
    const qDebounced = useDebouncedValue(q, 350);

    // Filter option lists (existing endpoints)
    const [depts, setDepts]             = useState([]);
    const [supervisors, setSupervisors] = useState([]);
    useEffect(() => {
        if (!canHr) return;   // HR-only lookups (the My-team view has no such filters)
        api.get('registration/departments')
            .then((r) => setDepts(Array.isArray(r.data) ? r.data : []))
            .catch(() => {});
        api.get('registration/supervisors')
            .then((r) => setSupervisors(Array.isArray(r.data) ? r.data : []))
            .catch(() => {});
    }, [canHr]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { scope, kind, page, pageSize };
            if (dept && !isMine)         params.dept = dept;
            if (supervisorId && !isMine) params.supervisorId = supervisorId;
            if (qDebounced)   params.q = qDebounced;
            if (from)         params.from = from;
            if (to)           params.to = to;
            const r = await api.get('approvals/queue', { params });
            setData(r.data || null);
        } catch (e) {
            setData(null);
            toast.error(e?.response?.data?.message || 'Failed to load approvals.');
        } finally { setLoading(false); }
    }, [scope, isMine, kind, dept, supervisorId, qDebounced, from, to, page]);

    useEffect(() => { load(); }, [load]);
    // Any filter change returns to page 1.
    useEffect(() => { setPage(1); }, [scope, kind, dept, supervisorId, qDebounced, from, to]);
    // My team has only FTO / COA.
    useEffect(() => { if (isMine && (kind === 'Profile' || kind === 'MissingPunch')) setKind('All'); }, [isMine, kind]);

    // ── Decision modal (FTO / COA / MissingPunch approve|deny) ──────────────
    const [decision, setDecision] = useState(null); // { item, approve }
    const [note, setNote]         = useState('');
    const [saving, setSaving]     = useState(false);

    const openDecision = (item, approve) => { setDecision({ item, approve }); setNote(''); };
    const closeDecision = () => { if (!saving) { setDecision(null); setNote(''); } };

    const submitDecision = async () => {
        if (!decision) return;
        const { item, approve } = decision;
        setSaving(true);
        try {
            if (item.kind === 'FTO' || item.kind === 'COA') {
                // Echo the CURRENT record and change only this row's decision:
                // the service writes whole branches for every role the caller
                // holds, so sending one flag alone would wipe the others (e.g.
                // an HR user who is also the supervisor erasing the supervisor
                // approval — the old workbench's bug).
                const base = item.kind === 'FTO' ? 'fto' : 'coa';
                const cur = (await api.get(`${base}/${item.id}`)).data || {};
                const body = {
                    supervisorApproval: cur.supervisorApproval,
                    supervisorNote:     cur.supervisorNote || '',
                    hrApproval:         cur.hrApproval,
                    hrNote:             cur.hrNote || '',
                };
                if (item.kind === 'FTO') {
                    body.mpcApproval = cur.mpcApproval;
                    body.exemption   = !!cur.exemption;
                }
                if (item.gate === 'supervisor') {
                    body.supervisorApproval = approve;
                    body.supervisorNote     = note || cur.supervisorNote || '';
                } else {
                    body.hrApproval = approve;
                    body.hrNote     = note || cur.hrNote || '';
                    // No supervisor decision yet: HR decides for both, so the
                    // request is final (FTO deducted, e-mails sent).
                    if (item.gate === 'hr-override') body.hrOverrideSupervisor = true;
                }
                await api.patch(`${base}/${item.id}/approvals`, body);
            } else if (item.kind === 'MissingPunch') {
                await api.post(
                    approve ? 'Punch/pending-approve' : 'Punch/pending-deny',
                    { punchId: item.id });
            }
            toast.success(`${KIND_META[item.kind]?.label || item.kind} ${approve ? 'approved' : 'denied'}.`);
            closeDecision();
            load();
        } catch (e) {
            toast.error(e?.response?.data?.message || 'Action failed.');
        } finally { setSaving(false); }
    };

    const counts = data?.counts || {};
    const pills = useMemo(() => ([
        { key: 'All',          count: (counts.fto || 0) + (counts.coa || 0) + (counts.profile || 0) + (counts.missingPunch || 0) },
        { key: 'FTO',          count: counts.fto || 0 },
        { key: 'COA',          count: counts.coa || 0 },
        { key: 'Profile',      count: counts.profile || 0, hrOnly: true },
        { key: 'MissingPunch', count: counts.missingPunch || 0, hrOnly: true },
    ].filter((p) => !(isMine && p.hrOnly))), [counts, isMine]);

    const items = data?.items || [];
    const total = data?.total || 0;
    const lastPage = Math.max(1, Math.ceil(total / pageSize));

    const exportCsv = () => exportRowsToCsv(
        isMine ? 'my-team-approvals.csv' : 'hr-review-queue.csv',
        [
            { key: 'kind',           label: 'Type' },
            { key: 'empId',          label: 'EmpID' },
            { key: 'empName',        label: 'Employee' },
            { key: 'dept',           label: 'Dept' },
            { key: 'supervisorName', label: 'Supervisor' },
            { label: 'Date', get: (r) => fmtDate(r.date) },
            { key: 'agingDays',      label: 'Aging (days)' },
            { key: 'status',         label: 'Status' },
            { key: 'summary',        label: 'Summary' },
        ],
        items);

    const rowActions = (item) => {
        if (item.kind === 'Profile') {
            return (
                <Link to="/hr/profile-approvals" className="btn btn-sm btn-soft-primary">
                    <i className="ri-eye-line align-bottom me-1" />Review
                </Link>
            );
        }
        return (
            <div className="d-flex gap-1 justify-content-end">
                <Button size="sm" color="success" outline onClick={() => openDecision(item, true)}>
                    <i className="ri-check-line align-bottom" /> Approve
                </Button>
                <Button size="sm" color="danger" outline onClick={() => openDecision(item, false)}>
                    <i className="ri-close-line align-bottom" /> Deny
                </Button>
            </div>
        );
    };

    return (
        <React.Fragment>
            <ToastContainer />
            <div className="page-content" style={{ background: 'var(--ssw-surface-2, #f5f5f5)', minHeight: '100vh' }}>
                <Container fluid>
                    <DashboardPageHeader
                        icon="ri-check-double-line"
                        eyebrow="Approvals"
                        title="Approvals"
                        subtitle={isMine
                            ? "FTO and COA requests from your team waiting for your decision."
                            : "Every FTO, COA, profile, and missing-punch item waiting on HR — in one queue."}
                        tone="violet"
                        breadcrumb={[
                            { label: 'Dashboards', to: '/' },
                            { label: 'Approvals' },
                        ]}
                    />

                    <Card>
                        <CardBody>
                            {/* ── Scope: My team / HR (HR tab only for HR / IT) ── */}
                            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                                <div className="btn-group" role="group" aria-label="Approvals view">
                                    <Button size="sm" color={isMine ? 'primary' : 'light'} onClick={() => setScope('mine')}>
                                        <i className="ri-team-line align-bottom me-1" />My team
                                    </Button>
                                    {canHr && (
                                        <Button size="sm" color={!isMine ? 'primary' : 'light'} onClick={() => setScope('hr')}>
                                            <i className="ri-shield-user-line align-bottom me-1" />HR
                                        </Button>
                                    )}
                                </div>
                                <small className="text-muted">
                                    Showing requests from the last 90 days and upcoming — pick an earlier From date to see older ones.
                                </small>
                            </div>

                            {/* ── Kind pills ── */}
                            <div className="d-flex flex-wrap gap-2 mb-3">
                                {pills.map((p) => (
                                    <Button
                                        key={p.key}
                                        size="sm"
                                        color={kind === p.key ? (KIND_META[p.key]?.color || 'primary') : 'light'}
                                        onClick={() => setKind(p.key)}
                                    >
                                        {KIND_META[p.key]?.label || p.key}
                                        <Badge color="light" className="text-dark ms-2">{p.count}</Badge>
                                    </Button>
                                ))}
                                <div className="ms-auto d-flex gap-2">
                                    <Button size="sm" color="light" onClick={load} disabled={loading}>
                                        <i className="ri-refresh-line align-bottom me-1" />Refresh
                                    </Button>
                                    <Button size="sm" color="light" onClick={exportCsv} disabled={!items.length}>
                                        <i className="ri-download-2-line align-bottom me-1" />CSV
                                    </Button>
                                </div>
                            </div>

                            {/* ── Filters ── */}
                            <Row className="g-2 mb-3">
                                <Col md={3}>
                                    <Input
                                        bsSize="sm" placeholder="Search employee, EmpID, supervisor…"
                                        value={q} onChange={(e) => setQ(e.target.value)}
                                    />
                                </Col>
                                {!isMine && (<>
                                <Col md={3}>
                                    <Input type="select" bsSize="sm" value={dept} onChange={(e) => setDept(e.target.value)}>
                                        <option value="">All departments</option>
                                        {depts.map((d) => (
                                            <option key={d.id} value={d.id}>{d.id} — {d.description}</option>
                                        ))}
                                    </Input>
                                </Col>
                                <Col md={3}>
                                    <Input type="select" bsSize="sm" value={supervisorId} onChange={(e) => setSupervisorId(e.target.value)}>
                                        <option value="">All supervisors</option>
                                        {supervisors.map((s) => (
                                            <option key={s.empID} value={s.empID}>{s.name}</option>
                                        ))}
                                    </Input>
                                </Col>
                                </>)}
                                <Col md={3}>
                                    <div className="d-flex gap-1 align-items-center">
                                        <Input type="date" bsSize="sm" value={from} onChange={(e) => setFrom(e.target.value)} />
                                        <span className="text-muted">–</span>
                                        <Input type="date" bsSize="sm" value={to} onChange={(e) => setTo(e.target.value)} />
                                    </div>
                                </Col>
                            </Row>

                            {/* ── Queue table ── */}
                            {loading && !data ? (
                                <div className="table-responsive">
                                    <Table size="sm" className="mb-0"><tbody>
                                        <TableSkeleton rows={6} columns={8} />
                                    </tbody></Table>
                                </div>
                            ) : !items.length ? (
                                <EmptyState
                                    icon="ri-checkbox-circle-line"
                                    title={isMine ? "Nothing waiting for your decision" : "Nothing needs HR attention"}
                                    message="No pending items match the current filters."
                                    tone="success"
                                />
                            ) : (
                                <div className="table-responsive">
                                    <Table hover size="sm" className="align-middle mb-2">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Type</th>
                                                <th>Employee</th>
                                                <th>Dept</th>
                                                <th>Supervisor</th>
                                                <th>Date</th>
                                                <th>Aging</th>
                                                <th>Summary</th>
                                                <th className="text-end">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.map((it) => (
                                                <tr key={`${it.kind}-${it.id}`}>
                                                    <td>{kindBadge(it.kind)}</td>
                                                    <td>
                                                        <div className="fw-medium">{it.empName || it.empId}</div>
                                                        <small className="text-muted">{it.empId}</small>
                                                    </td>
                                                    <td>{it.dept || '—'}</td>
                                                    <td>{it.supervisorName || '—'}</td>
                                                    <td>{fmtDate(it.date)}</td>
                                                    <td>
                                                        <Badge color={it.agingDays > 7 ? 'danger' : it.agingDays > 2 ? 'warning' : 'light'}
                                                               className={it.agingDays <= 2 ? 'text-dark' : ''}>
                                                            {it.agingDays}d
                                                        </Badge>
                                                    </td>
                                                    <td className="text-muted">{it.summary}</td>
                                                    <td className="text-end">{rowActions(it)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}

                            {/* ── Pager ── */}
                            {total > pageSize && (
                                <div className="d-flex align-items-center justify-content-between">
                                    <small className="text-muted">
                                        {total} item(s) — page {page} of {lastPage}
                                    </small>
                                    <div className="d-flex gap-1">
                                        <Button size="sm" color="light" disabled={page <= 1 || loading}
                                                onClick={() => setPage((p) => p - 1)}>
                                            <i className="ri-arrow-left-s-line" />
                                        </Button>
                                        <Button size="sm" color="light" disabled={page >= lastPage || loading}
                                                onClick={() => setPage((p) => p + 1)}>
                                            <i className="ri-arrow-right-s-line" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>

            {/* ── Approve / deny confirmation with optional HR note ── */}
            <Modal isOpen={!!decision} toggle={closeDecision} centered>
                <ModalHeader toggle={closeDecision}>
                    {decision?.approve ? 'Approve' : 'Deny'}{' '}
                    {KIND_META[decision?.item?.kind]?.label || decision?.item?.kind}
                </ModalHeader>
                <ModalBody>
                    <p className="mb-2">
                        <strong>{decision?.item?.empName || decision?.item?.empId}</strong>
                        {' — '}{decision?.item?.summary}
                    </p>
                    {decision?.item?.gate === 'hr-override' && (
                        <p className="small text-warning mb-2">
                            <i className="ri-error-warning-line align-bottom me-1" />
                            The supervisor hasn't decided yet — your decision is recorded for the supervisor
                            step too (HR override), so the request is final.
                        </p>
                    )}
                    {decision?.item?.kind !== 'MissingPunch' && (
                        <FormGroup className="mb-0">
                            <Label className="form-label" for="hr-review-note">
                                {decision?.item?.gate === 'supervisor' ? 'Supervisor note (optional)' : 'HR note (optional)'}
                            </Label>
                            <Input
                                id="hr-review-note" type="textarea" rows={2}
                                value={note} onChange={(e) => setNote(e.target.value)}
                            />
                        </FormGroup>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={closeDecision} disabled={saving}>Cancel</Button>
                    <Button color={decision?.approve ? 'success' : 'danger'} onClick={submitDecision} disabled={saving}>
                        {saving ? <Spinner size="sm" /> : (decision?.approve ? 'Approve' : 'Deny')}
                    </Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
};

export default HrReviewDashboard;
